<!--
    REV-21370 — the detail surface of one correction booking (Pure View), ADR-0015 §4 / AC8 / AC9.

    Layout follows the prototype: a `Zurück` control, the booking text as the heading, a definition
    table of every field, then the controls under the SAME condition as in the list. What the prototype
    has no notion of is the ambiguity picker — §4 added it, and it is the only control in this whole
    feature that leads to `Gebucht`.
-->
<section class="adjd" data-testid="adjustment-detail">
    <div class="adjd-top" *ngIf="showBack">
        <ham-button
            data-testid="adjustment-detail-back"
            variant="white"
            icon="assets/hamIcons/x.svg"
            (buttonClick)="onBackClick()"
        >
            {{ 'abcp_adjustments_detail_back' | i18next }}
        </ham-button>
    </div>

    <!-- Loading: placeholders in the SHAPE of the panel, so nothing jumps (AC11). -->
    <div class="adjd-panel" data-testid="adjustment-detail-loading" *ngIf="loading">
        <div class="adjd-skeleton adjd-skeleton--title" aria-hidden="true"></div>
        <div class="adjd-skeleton" *ngFor="let line of [1, 2, 3, 4, 5]; trackBy: trackBySelf" aria-hidden="true"></div>
    </div>

    <!-- Error: a short note with a way to try again (AC11). -->
    <div class="adjd-panel adjd-panel--error" data-testid="adjustment-detail-error" *ngIf="!loading && error">
        <span>{{ 'abcp_adjustments_error' | i18next }}</span>
        <button type="button" class="adjd-link" data-testid="adjustment-detail-retry" (click)="onRetryClick()">
            {{ 'abcp_adjustments_retry' | i18next }}
        </button>
    </div>

    <div class="adjd-panel" *ngIf="!loading && !error && adjustment">
        <!--
            ⚠️ **The layout is IDENTICAL in both modes.** Every editable value keeps its row, its
            label and its position; only the value itself becomes a control. That is the whole point of
            editing in place: the reader does not have to re-find the field they came for, and the rest
            of the record stays visible while one line changes.
        -->
        <h2 class="adjd-title" data-testid="adjustment-detail-title" *ngIf="!isEditing">{{ adjustment.text }}</h2>
        <input
            class="adjd-title adjd-input adjd-input--title"
            data-testid="adjustment-detail-text-input"
            *ngIf="isEditing"
            [attr.aria-label]="'abcp_adjustments_col_text' | i18next"
            [(ngModel)]="draftText"
        />

        <table class="adjd-table" data-testid="adjustment-detail-fields">
            <tbody>
                <tr>
                    <th scope="row">{{ 'abcp_adjustments_col_state' | i18next }}</th>
                    <td>
                        <abcp-adjustment-status
                            [state]="adjustment.state"
                            [ambiguous]="!!adjustment.ambiguous"
                            [notFound]="!!adjustment.notFound"
                        ></abcp-adjustment-status>
                    </td>
                </tr>
                <tr>
                    <th scope="row">{{ 'abcp_adjustments_col_debit' | i18next }}</th>
                    <td data-testid="adjustment-detail-debit">
                        <span *ngIf="!isEditing || !canPickAccounts"
                            >{{ adjustment.debit.number }} {{ adjustment.debit.name }}</span
                        >
                        <!--
                            No options means read-only rather than an empty picker: a select with
                            nothing in it is a dead control that looks live.
                        -->
                        <select
                            class="adjd-input"
                            data-testid="adjustment-detail-debit-input"
                            *ngIf="isEditing && canPickAccounts"
                            [attr.aria-label]="'abcp_adjustments_col_debit' | i18next"
                            [(ngModel)]="draftDebitAccountId"
                        >
                            <option
                                *ngFor="let account of accounts; trackBy: trackByAccountId"
                                [value]="account.id"
                            >
                                {{ accountLabel(account) }}
                            </option>
                        </select>
                    </td>
                </tr>
                <tr>
                    <th scope="row">{{ 'abcp_adjustments_col_credit' | i18next }}</th>
                    <td data-testid="adjustment-detail-credit">
                        <span *ngIf="!isEditing || !canPickAccounts"
                            >{{ adjustment.credit.number }} {{ adjustment.credit.name }}</span
                        >
                        <select
                            class="adjd-input"
                            data-testid="adjustment-detail-credit-input"
                            *ngIf="isEditing && canPickAccounts"
                            [attr.aria-label]="'abcp_adjustments_col_credit' | i18next"
                            [(ngModel)]="draftCreditAccountId"
                        >
                            <option
                                *ngFor="let account of accounts; trackBy: trackByAccountId"
                                [value]="account.id"
                            >
                                {{ accountLabel(account) }}
                            </option>
                        </select>
                    </td>
                </tr>
                <tr>
                    <th scope="row">{{ 'abcp_adjustments_col_amount' | i18next }}</th>
                    <td class="adjd-amount" data-testid="adjustment-detail-amount">
                        <span *ngIf="!isEditing"
                            >{{ adjustment.amount.currency }}&nbsp;{{
                                adjustment.amount.minor / 100 | finNumber: '1.2-2'
                            }}</span
                        >
                        <ng-container *ngIf="isEditing">
                            <!-- The currency stays TEXT: it is the closing's, not a per-row choice. -->
                            {{ adjustment.amount.currency }}&nbsp;<input
                                class="adjd-input adjd-input--amount"
                                data-testid="adjustment-detail-amount-input"
                                inputmode="decimal"
                                placeholder="0.00"
                                [attr.aria-label]="'abcp_adjustments_col_amount' | i18next"
                                [(ngModel)]="draftAmountInput"
                            />
                        </ng-container>
                    </td>
                </tr>
                <tr>
                    <th scope="row">{{ 'abcp_adjustments_field_owner' | i18next }}</th>
                    <td>
                        <!-- A way to the affected account (AC8). A button, so it is keyboard-reachable. -->
                        <button type="button" class="adjd-link" data-testid="adjustment-detail-account" (click)="onAccountClick()">
                            {{ (adjustment.owner ?? adjustment.debit).number }}
                            · {{ (adjustment.owner ?? adjustment.debit).name }}
                        </button>
                    </td>
                </tr>
                <!--
                    `Quelle` (§8). A provenance FACT with the name of whoever entered or last edited it —
                    and nothing else. No confidence, no reasoning trail, no proposal state, no
                    accept/reject: the column is a source, not half a proposal flow.
                -->
                <tr>
                    <th scope="row">{{ 'abcp_adjustments_col_source' | i18next }}</th>
                    <td data-testid="adjustment-detail-source">
                        <span class="adjd-source" [attr.data-source]="adjustment.source">{{
                            sourceLabelKey | i18next
                        }}</span>
                        <span class="adjd-source__name" *ngIf="sourceName">{{ sourceName }}</span>
                    </td>
                </tr>
                <tr>
                    <th scope="row">{{ 'abcp_adjustments_col_date' | i18next }}</th>
                    <td data-testid="adjustment-detail-date">
                        <span *ngIf="!isEditing">{{ adjustment.date | date: 'dd.MM.yyyy' }}</span>
                        <!--
                            The BOOKING date, editable inside the fiscal year (V2/AC3). Not the
                            creation timestamp — that one is never shown here and never editable.
                        -->
                        <input
                            class="adjd-input"
                            data-testid="adjustment-detail-date-input"
                            type="date"
                            *ngIf="isEditing"
                            [attr.aria-label]="'abcp_adjustments_field_date' | i18next"
                            [(ngModel)]="draftDate"
                        />
                    </td>
                </tr>
                    <!--
                        No `MWST` row (03.09.2026). Same reason it left the account list and the
                        register: the entry dialog has no VAT field and the entity refuses to invent
                        one, so the row read `—` on every correction it could ever show. A field that
                        is structurally empty looks like missing data rather than an absent fact.
                        It stays on the MODEL and on the Kontoblatt, where an ERP journal line
                        genuinely carries a code.
                    -->
                <tr>
                    <th scope="row">{{ 'abcp_adjustments_field_remark' | i18next }}</th>
                    <td data-testid="adjustment-detail-remark">
                        <span *ngIf="!isEditing">{{ adjustment.remark || '—' }}</span>
                        <input
                            class="adjd-input"
                            data-testid="adjustment-detail-remark-input"
                            *ngIf="isEditing"
                            [attr.aria-label]="'abcp_adjustments_field_remark' | i18next"
                            [(ngModel)]="draftRemark"
                        />
                    </td>
                </tr>
            </tbody>
        </table>

        <!--
            ⚠️ THE AMBIGUITY PICKER — ADR-0015 §4, AC9.

            The reconciliation found SEVERAL journal lines matching debit account, credit account,
            amount and fiscal year, and it guessed nothing: a guessed assignment consumes a real journal
            line for the wrong correction, and the error is afterwards only findable on paper.

            RADIO inputs, so exactly one line can be chosen and the group is keyboard-operable for free.
            Every `[value]` is a candidate id the server sent — there is no text field here, which is
            what makes "the user cannot invent a line" structural rather than a promise.
        -->
        <div class="adjd-ambiguity" data-testid="adjustment-detail-ambiguity" *ngIf="isAmbiguous">
            <h3 class="adjd-ambiguity__title">{{ 'abcp_adjustments_ambiguity_title' | i18next }}</h3>
            <p class="adjd-ambiguity__hint">{{ 'abcp_adjustments_ambiguity_hint' | i18next }}</p>

            <ul class="adjd-candidates" role="radiogroup" [attr.aria-label]="'abcp_adjustments_ambiguity_title' | i18next">
                <li class="adjd-candidate" *ngFor="let candidate of candidates; trackBy: trackByCandidate">
                    <label class="adjd-candidate__label" data-testid="adjustment-candidate">
                        <input
                            type="radio"
                            name="adjustment-candidate"
                            data-testid="adjustment-candidate-radio"
                            [value]="candidate.journalEntryId"
                            [checked]="selectedCandidateId === candidate.journalEntryId"
                            [disabled]="readOnly || pending"
                            (change)="onCandidateSelect(candidate.journalEntryId)"
                        />
                        <span class="adjd-candidate__date">{{ candidate.date | date: 'dd.MM.yyyy' }}</span>
                        <span class="adjd-candidate__text">{{ candidate.description || '—' }}</span>
                        <span class="adjd-candidate__amount"
                            >{{ candidate.amount.currency }}&nbsp;{{ candidate.amount.minor / 100 | finNumber: '1.2-2' }}</span
                        >
                        <!-- The ERP's document number, or `—`: the normalised journal holds none today. -->
                        <span class="adjd-candidate__doc">{{ candidate.documentNumber || '—' }}</span>
                    </label>
                </li>
            </ul>

            <ham-button
                data-testid="adjustment-candidate-confirm"
                variant="primary"
                size="sm"
                [disabled]="!canResolveAmbiguity"
                (buttonClick)="onResolveClick()"
            >
                {{ 'abcp_adjustments_ambiguity_confirm' | i18next }}
            </ham-button>
        </div>

        <!--
            The controls — under the SAME conditions as in the list (AC6/AC7). There is no second rule,
            and `editable` is the backend's answer rather than this view's derivation (§7).
        -->
        <!--
            A WITHDRAWN correction offers exactly one action: put it back. `Verworfen` is a state of
            circulation, not a delete, and without a way back it would be a delete with extra steps.
            Rendered outside `canModify`, because the backend answers `editable: false` for a withdrawn
            row — it may not be changed, only restored.
        -->
        <div class="adjd-actions" data-testid="adjustment-detail-discarded-actions" *ngIf="isDiscarded && !readOnly">
            <span
                class="adjd-discarded"
                [hamTooltip]="'abcp_adjustments_discarded_hint' | i18next"
                tooltipClass="abcp"
                >{{ 'abcp_adjustments_discarded' | i18next }}</span
            >
            <ham-button
                data-testid="adjustment-detail-restore"
                variant="primary"
                [disabled]="pending"
                [hamTooltip]="'abcp_adjustments_restore_hint' | i18next"
                tooltipClass="abcp"
                (buttonClick)="onRestoreClick()"
            >
                {{ 'abcp_adjustments_restore' | i18next }}
            </ham-button>
        </div>

        <!-- A refusal from the last save, above the buttons where the eye already is. -->
        <p class="adjd-error" data-testid="adjustment-detail-edit-error" *ngIf="editErrorKey">
            {{ editErrorKey | i18next }}
        </p>

        <div class="adjd-actions" data-testid="adjustment-detail-actions" *ngIf="canModify">
            <!-- `Bearbeiten` starts in-place editing; it does not open a dialog any more. -->
            <ham-button
                data-testid="adjustment-detail-edit"
                *ngIf="!isEditing"
                variant="white"
                (buttonClick)="onEditClick()"
            >
                {{ 'abcp_adjustments_edit_start' | i18next }}
            </ham-button>
            <ham-button
                data-testid="adjustment-detail-save"
                *ngIf="isEditing"
                variant="primary"
                [disabled]="!canEditSave"
                (buttonClick)="onEditSave()"
            >
                {{ 'abcp_adjustments_edit_save' | i18next }}
            </ham-button>
            <ham-button
                data-testid="adjustment-detail-cancel"
                *ngIf="isEditing"
                variant="white"
                (buttonClick)="onEditCancel()"
            >
                {{ 'abcp_adjustments_edit_cancel' | i18next }}
            </ham-button>
            <ham-button
                data-testid="adjustment-detail-discard"
                variant="red"
                [outline]="true"
                (buttonClick)="onDiscardClick()"
            >
                {{ 'abcp_adjustments_discard' | i18next }}
            </ham-button>

            <div class="adjd-actions__spacer"></div>

            <!-- The tooltip sits on the host: the disabled inner button takes no pointer events. -->
            <ham-button
                data-testid="adjustment-detail-transfer"
                *ngFor="let method of transferTargets; trackBy: trackBySelf"
                variant="white"
                [attr.data-method]="method"
                [disabled]="!isTransferTargetEnabled(method)"
                [hamTooltip]="transferReasonKey(method) ? (transferReasonKey(method) | i18next) : ''"
                tooltipClass="abcp"
                (buttonClick)="onTransferClick(method)"
            >
                {{ transferTitleKey(method) | i18next }}
            </ham-button>
        </div>

        <!--
            The one sentence §2 makes MANDATORY rather than optional: after a transfer the row stays on
            `Übertragen` until the next ERP import, and the surface has to explain that delay. The
            prototype's own progress display already words it this way.
        -->
        <p class="adjd-note" data-testid="adjustment-detail-proof-note" *ngIf="adjustment.state === 'transferred'">
            {{ 'abcp_adjustments_proof_pending' | i18next }}
        </p>
    </div>
</section>
